// MapLibre GL JS style configs — Carte v5 (Iter85, Esri World Imagery satellite).
//
// We use MapTiler vector tiles (streets-v2, streets-v2-dark) for Plan/Sombre
// when `REACT_APP_MAPTILER_KEY` is defined, and fall back to OpenFreeMap
// when it isn't. Satellite ("hybrid") is now ALWAYS Esri World Imagery,
// regardless of the MapTiler key — MapTiler's satellite tiles were too
// blurry at z18-19 in Bamako; Esri's are noticeably sharper at the same zoom.
//
// "street"   = Plan       — MapTiler streets-v2   / OpenFreeMap Liberty
// "dark"     = Sombre     — MapTiler streets-v2-dark / OpenFreeMap Dark
// "hybrid"   = Satellite  — Esri World Imagery (always, no MapTiler dependency)
//
// ── Esri World Imagery endpoint note (Sep 2026) ─────────────────────────
// Migrated to Esri's officially recommended production endpoint,
// `ibasemaps-api.arcgis.com` (ArcGIS Location Platform basemap service),
// authenticated with an API key the user generated via their ArcGIS
// developer account (free tier ≈2M tile requests/month). No longer using
// the legacy unauthenticated `server.arcgisonline.com` endpoint.
const MAPTILER_KEY = (process.env.REACT_APP_MAPTILER_KEY || "").trim();
const MAPTILER_ENABLED = MAPTILER_KEY.length > 0;
const ESRI_API_KEY = (process.env.REACT_APP_ESRI_API_KEY || "").trim();

// MapTiler auto-serves @2x tiles when the browser DPR>1 — no config needed.
const MAPTILER_STYLES = {
  streets: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`,
  dark:    `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${MAPTILER_KEY}`,
};

const OPENFREEMAP_STYLES = {
  liberty: "https://tiles.openfreemap.org/styles/liberty",
  positron: "https://tiles.openfreemap.org/styles/positron",
  bright: "https://tiles.openfreemap.org/styles/bright",
  dark: "https://tiles.openfreemap.org/styles/dark",
};

// Esri's required attribution for World Imagery (ToS §3) — must list all
// data contributors, not just "Esri".
const ATTRIB_ESRI =
  '<a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a>, Maxar, Earthstar Geographics, GIS User Community';
const ATTRIB_OPENFREEMAP =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> · <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

const ESRI_SAT_TILES = [
  ESRI_API_KEY
    ? `https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${ESRI_API_KEY}`
    // Dev-only fallback if no key is configured — legacy, unauthenticated.
    : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
];

// Verified by direct tile probing over Bamako (12.6392, -8.0029) on Aug 2026:
// z18/z19 return real imagery (~19-20 KB tiles); z20/z21 return Esri's
// blank "map data not yet available" placeholder (~2.5 KB, identical at
// both zooms). 19 is therefore the true native ceiling for Mali today —
// capping the whole app there (rather than a global/theoretical z22)
// prevents MapLibre from auto-stretching the last real tile into a blurry
// "fake zoom", which is exactly the artifact we're replacing MapTiler to
// avoid. Since Bamako (the capital, best-covered area) already tops out
// at 19, no other region of Mali can exceed it.
export const ESRI_MAX_NATIVE_ZOOM = 19;

/** Applies the Esri native-resolution ceiling only while Satellite is the
 *  active layer; other styles (vector, higher native zoom) are uncapped.
 *  Safe to call any time — does not require the style to be loaded. */
export const applySatelliteZoomCap = (map, kind) => {
  try { map.setMaxZoom(kind === "hybrid" ? ESRI_MAX_NATIVE_ZOOM : 22); }
  catch (_e) { /* map not ready yet — noop */ }
};

/** Satellite style: Esri World Imagery raster, no vector labels overlay
 *  (kept as a clean, unobstructed 2D image — see installBuildingExtrusion
 *  below for why buildings/labels are intentionally skipped in this mode). */
export const STYLE_HYBRID_FALLBACK = {
  version: 8,
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
  sources: {
    "esri-sat": {
      type: "raster",
      tiles: ESRI_SAT_TILES,
      tileSize: 256,
      attribution: ATTRIB_ESRI,
      maxzoom: ESRI_MAX_NATIVE_ZOOM,
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#0b0f14" } },
    { id: "esri-sat", type: "raster", source: "esri-sat" },
  ],
};

// Back-compat export alias — legacy imports still reference STYLE_HYBRID.
export const STYLE_HYBRID = STYLE_HYBRID_FALLBACK;

/**
 * `getStyle` returns either a style URL (vector) or a style object (hybrid).
 * MapLibre accepts both. For `setStyle`, callers must wait on `styledata`
 * before re-installing custom sources/layers — same as before.
 */
export const getStyle = (kind) => {
  // Satellite is always Esri World Imagery — independent of MapTiler key.
  if (kind === "hybrid") return STYLE_HYBRID_FALLBACK;
  if (MAPTILER_ENABLED) {
    if (kind === "dark") return MAPTILER_STYLES.dark;
    return MAPTILER_STYLES.streets; // default: MapTiler streets-v2 (Plan)
  }
  // Free/no-key fallback
  if (kind === "dark")   return OPENFREEMAP_STYLES.dark;
  if (kind === "bright") return OPENFREEMAP_STYLES.bright;
  return OPENFREEMAP_STYLES.liberty;
};

/**
 * Inject a 3D building extrusion layer. Call AFTER the style has loaded
 * (i.e. inside `map.on('styledata', ...)`).
 *
 * Sep 2026 — MUST be skipped entirely for `hybrid` (Satellite). The old
 * comment here assumed hybrid "doesn't have building geometry" because
 * it has no vector source — WRONG for MapTiler: the hybrid style.json
 * still ships a vector source (`maptiler_planet`, used to overlay road/
 * place labels on top of the raster satellite imagery) whose tiles
 * DO contain a `building` source-layer, even though the hybrid style's
 * OWN layer list never renders it. Since this function adds its OWN
 * fill-extrusion layer straight from that source-layer, it was extruding
 * real building footprints as solid gray blocks directly on top of the
 * satellite imagery — exactly the "houses covered by gray shapes" bug.
 * The `layer` param is the caller's authoritative source of truth for
 * which mode is active; we no longer try to infer it from the style.
 *
 * Vector styles from OpenFreeMap / MapTiler streets expose a `building`
 * source-layer with `render_height` / `render_min_height` properties —
 * we drive extrusion directly from those, but ONLY for `street`/`dark`.
 *
 * Extrusion only renders at zoom ≥ 14 so distant city silhouettes stay
 * flat and crisp.
 */
export const installBuildingExtrusion = (map, layer) => {
  if (layer === "hybrid") return; // Satellite must stay a clean, unobstructed 2D image.
  // Find the OpenMapTiles vector source already loaded by the style.
  // Both MapTiler (`https://api.maptiler.com/tiles/...`) and OpenFreeMap
  // (`https://tiles.openfreemap.org/...`) publish the OpenMapTiles schema,
  // so the extrusion layer + source-layer `building` works on both once
  // we detect the correct source id (which the two providers name
  // differently — MapTiler often uses `openmaptiles`, OpenFreeMap uses
  // an arbitrary auto-generated id).
  const style = map.getStyle();
  if (!style || !style.sources) return;
  const vectorSourceId = Object.entries(style.sources).find(
    ([, s]) =>
      s.type === "vector" &&
      (
        (s.url || "").includes("openfreemap") ||
        (s.url || "").includes("maptiler") ||
        (s.url || "").includes("openmaptiles")
      )
  )?.[0];
  if (!vectorSourceId) return;  // no vector source found — nothing to extrude

  if (map.getLayer("3d-buildings")) return;
  // Insert beneath label layers so street names stay on top.
  let beforeId;
  for (const layer of style.layers || []) {
    const id = layer.id || "";
    if (id.includes("place") || id.includes("label") || id.includes("poi")) {
      beforeId = id;
      break;
    }
  }
  try {
    map.addLayer(
      {
        id: "3d-buildings",
        source: vectorSourceId,
        "source-layer": "building",
        type: "fill-extrusion",
        minzoom: 13,
        paint: {
          "fill-extrusion-color": [
            "case",
            ["boolean", ["feature-state", "highlighted"], false],
            "#10B981",
            "#3b465a",
          ],
          "fill-extrusion-height": [
            "interpolate", ["linear"], ["zoom"],
            13, 0,
            13.5, ["coalesce", ["get", "render_height"], 8],
            16, ["coalesce", ["get", "render_height"], 12],
            18, ["coalesce", ["get", "render_height"], 14],
          ],
          "fill-extrusion-base": ["coalesce", ["get", "render_min_height"], 0],
          "fill-extrusion-opacity": 0.85,
          "fill-extrusion-vertical-gradient": true,
        },
      },
      beforeId,
    );
  } catch (_e) { /* style may not be fully ready; safe to skip */ }
};

export { OPENFREEMAP_STYLES, ATTRIB_OPENFREEMAP };
